import { useCallback, useMemo } from 'react';
import { createFileRoute, Link, Outlet, useRouter } from '@tanstack/react-router';
import { BarChart3, Plus } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { useHasPermission } from '@/hooks/usePermissions';
import { WorkspaceShell, useWorkspaceHeader } from '@/components/layouts';
import { Fullbleed } from '@/contexts/fullbleed';
import { useEntityRouteState } from '@/hooks/navigation/useEntityRouteState';
import {
  DEFAULT_TECHNICIAN_LIST_FILTERS,
  resolveTechnicianListFilters,
  TECHNICIAN_SORT_OPTIONS,
  TECHNICIAN_STATUS_FILTERS,
  type TechnicianListFilters,
  type TechnicianStatusFilter,
} from '@/components/organisms/technicians/technician-list-utils';
import { TechnicianTableView } from '@/components/organisms/technicians/TechnicianTableView';

type TechnicianRouteSearch = Partial<TechnicianListFilters>;

export const Route = createFileRoute('/_dashboard/dashboard/technicians')({
  validateSearch: (search: Record<string, unknown>): TechnicianRouteSearch => ({
    q: typeof search.q === 'string' ? search.q : DEFAULT_TECHNICIAN_LIST_FILTERS.q,
    status: TECHNICIAN_STATUS_FILTERS.some((filter) => filter.value === search.status)
      ? (search.status as TechnicianStatusFilter)
      : DEFAULT_TECHNICIAN_LIST_FILTERS.status,
    expertise: typeof search.expertise === 'string' ? search.expertise : '',
    capacityMin: typeof search.capacityMin === 'string' ? search.capacityMin : '',
    capacityMax: typeof search.capacityMax === 'string' ? search.capacityMax : '',
    sort:
      typeof search.sort === 'number' && search.sort >= 0
        ? Math.min(Math.floor(search.sort), TECHNICIAN_SORT_OPTIONS.length - 1)
        : DEFAULT_TECHNICIAN_LIST_FILTERS.sort,
  }),
  component: TechniciansLayout,
});

function cleanSearch(next: Partial<TechnicianListFilters>) {
  const clean: Partial<TechnicianListFilters> = { ...next };
  const defaults: Partial<Record<keyof TechnicianListFilters, unknown>> = {
    q: '',
    status: 'all',
    expertise: '',
    capacityMin: '',
    capacityMax: '',
    sort: 0,
  };

  (Object.keys(clean) as Array<keyof TechnicianListFilters>).forEach((key) => {
    if (clean[key] == null || clean[key] === defaults[key]) delete clean[key];
  });

  return clean as TechnicianRouteSearch;
}

function TechniciansLayout() {
  const router = useRouter();
  const routeSearch = Route.useSearch();
  const filters = useMemo(() => resolveTechnicianListFilters(routeSearch), [routeSearch]);
  const { hasChildRoute, preservedSearch: listSearch } = useEntityRouteState({
    basePath: '/dashboard/technicians',
    search: filters,
    cleanSearch,
  });

  const handleSelect = useCallback(
    (id: string) => {
      void router.navigate({
        to: '/dashboard/technicians/$id',
        params: { id },
        search: listSearch,
      });
    },
    [listSearch, router],
  );

  const handleFiltersChange = useCallback(
    (patch: Partial<TechnicianListFilters>) => {
      void router.navigate({
        to: '/dashboard/technicians',
        search: cleanSearch({ ...filters, ...patch }),
        replace: true,
      });
    },
    [filters, router],
  );

  // Like Contacts: the technician record (and its forms + reports) render
  // full-screen — no split, no drawer. The list below is the full-screen table.
  if (hasChildRoute) return <Outlet />;

  return (
    <WorkspaceShell>
      <Fullbleed />
      <TechniciansHeaderSlots listSearch={listSearch} />
      <div className="flex h-full min-h-0 flex-col bg-card">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <TechnicianTableView
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onOpen={handleSelect}
          />
        </div>
      </div>
    </WorkspaceShell>
  );
}

// Injects the New/Reports actions into the global workspace header (mirrors
// Contacts) instead of a per-page title row. Renders nothing itself.
function TechniciansHeaderSlots({ listSearch }: { listSearch: TechnicianRouteSearch }) {
  const canViewReports = useHasPermission('technicians:view');

  const newButton = useMemo(
    () => (
      <Button size="sm" title="New technician" asChild>
        <Link to="/dashboard/technicians/new" search={listSearch}>
          <Plus size={14} />
          <span>
            New<span className="hidden sm:inline"> technician</span>
          </span>
        </Link>
      </Button>
    ),
    [listSearch],
  );

  const quickActions = useMemo(
    () =>
      canViewReports ? (
        <Button size="sm" variant="outline" title="Technician reports" asChild>
          <Link to="/dashboard/technicians/reports" search={(prev) => prev as never}>
            <BarChart3 size={14} />
            Reports
          </Link>
        </Button>
      ) : null,
    [canViewReports],
  );

  useWorkspaceHeader({ newButton, quickActions }, [newButton, quickActions]);
  return null;
}
